import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import { API_CONFIG } from 'shared/constants/constants';
import { State } from 'shared/interface';
import { AnalyticService } from 'shared/services/api.service';
import AuthService from 'shared/services/auth.service';
import { getDifferenceInMinutes } from 'shared/util/utility';

import { IAnalyticsStore } from 'features/login/interface/login';

export const useSessionTracking = () => {
	const benutzer_id = useSelector((state: State) => state.auth.userData?.benutzer_id);
	const admin = useSelector((state: State) => state.auth.userData?.admin);
	const isTakeOver = useSelector((state: State) => state.siteConfig?.isTakeOver);
	const { pathname } = useLocation();
	const intervalRef = useRef<any>(null);
	const timeOutRef = useRef<any>(null);
	const pageVisitTimeoutRef = useRef<any>(null);
	const latestPathRef = useRef(pathname);
	const lastTrackedPathRef = useRef<string | null>(null);

	const [isActiveTab, setIsActiveTab] = useState(true);

	const shouldTrack = !isTakeOver;
	const scheduleAnalytics = useCallback((fn: () => void) => {
		if (process.env.REACT_APP_DISABLE_ANALYTICS === 'true') return;
		const delayMs = Number(process.env.REACT_APP_ANALYTICS_INITIAL_DELAY_MS || 4000);
		const run = () => {
			const idleCallback = (window as any).requestIdleCallback as ((cb: () => void, options?: { timeout: number }) => void) | undefined;
			if (typeof idleCallback === 'function') {
				idleCallback(() => fn(), { timeout: delayMs + 2000 });
				return;
			}
			window.setTimeout(fn, delayMs);
		};

		if (document.readyState === 'complete') {
			run();
			return;
		}

		const onLoad = () => {
			window.removeEventListener('load', onLoad);
			run();
		};
		window.addEventListener('load', onLoad, { once: true });
	}, []);

	const isAdmin = useMemo(() => !isEmpty(admin), [admin]);

	const getValidDate = useCallback((value?: string) => {
		const d = value ? new Date(value) : new Date();
		return Number.isNaN(d.getTime()) ? new Date() : d;
	}, []);

	const userSessionTrackingAnalytics = useCallback(async () => {
		const analyticsStore = AuthService.getAnalyticsSessionStore() as IAnalyticsStore;
		const { sessionStreamUserId, startTime: sessionStartTime, endTime } = analyticsStore;
		const safeStartTime = getValidDate(sessionStartTime).toISOString();
		const sessionEndTime = new Date(getValidDate(endTime).getTime() + 10 * 60000).toISOString();

		const payload = {
			stream_id: process.env.REACT_APP_ANALYTICS_SESSION_STREAM_ID,
			data: {
				user_id: `${benutzer_id}`,
				session_started_at: safeStartTime,
				session_ended_at: sessionEndTime,
				minutes: getDifferenceInMinutes(safeStartTime, sessionEndTime),
				is_admin: isAdmin
			}
		};
		try {
			const response = await AnalyticService.post(
				API_CONFIG.path.userSessionAnalytics,
				payload,
				{},
				{
					apiKey: process.env.REACT_APP_ANALYTICS_API_KEY,
					streamUserId: sessionStreamUserId
				}
			);
			return {
				sessionStreamUserId: response?.stream_user_id || sessionStreamUserId || '',
				endTime: sessionEndTime,
				startTime: safeStartTime
			};
		} catch {
			return {
				sessionStreamUserId: sessionStreamUserId || '',
				endTime: sessionEndTime,
				startTime: safeStartTime
			};
		}
	}, [benutzer_id, getValidDate, isAdmin]);

	const trackingForEveryPageChange = useCallback(
		async (trackedPath?: string) => {
			const analyticsStore = AuthService.getAnalyticsSessionStore() as IAnalyticsStore;
			const { pageVisitsStreamId, sessionStreamUserId } = analyticsStore;
			const payload = {
				stream_id: process.env.REACT_APP_ANALYTICS_PAGE_VISIT_STREAM_ID,
				data: { user_id: `${benutzer_id}`, is_admin: isAdmin }
			};
			try {
				const response = await AnalyticService.post(
					API_CONFIG.path.userPageVisitAnalytics,
					payload,
					{},
					{
						apiKey: process.env.REACT_APP_ANALYTICS_API_KEY,
						streamUserId: pageVisitsStreamId
					}
				);
				if (!pageVisitsStreamId && sessionStreamUserId && response?.stream_user_id) {
					AuthService.setAnalyticsSessionStore({
						...analyticsStore,
						pageVisitsStreamId: response.stream_user_id
					});
				}
				lastTrackedPathRef.current = trackedPath || pathname;
				return { pageVisitsStreamId: response?.stream_user_id || pageVisitsStreamId || '' };
			} catch {
				return { pageVisitsStreamId: pageVisitsStreamId || '' };
			}
		},
		[benutzer_id, isAdmin, pathname]
	);

	const schedulePageVisit = useCallback(
		(pathToTrack: string, delayOverrideMs?: number) => {
			if (process.env.REACT_APP_DISABLE_ANALYTICS === 'true') return;
			if (!pathToTrack || pathToTrack.includes('login')) return;
			if (lastTrackedPathRef.current === pathToTrack) return;

			if (pageVisitTimeoutRef.current) {
				clearTimeout(pageVisitTimeoutRef.current);
			}

			const delayMs = delayOverrideMs ?? Number(process.env.REACT_APP_ANALYTICS_PAGE_VISIT_DELAY_MS || 7000);
			pageVisitTimeoutRef.current = setTimeout(() => {
				const run = () => {
					if (!shouldTrack || !isActiveTab) return;
					if (latestPathRef.current !== pathToTrack) return;
					trackingForEveryPageChange(pathToTrack);
				};

				const idleCallback = (window as any).requestIdleCallback as ((cb: () => void, options?: { timeout: number }) => void) | undefined;
				if (typeof idleCallback === 'function') {
					idleCallback(run, { timeout: delayMs + 2000 });
					return;
				}
				run();
			}, delayMs);
		},
		[isActiveTab, shouldTrack, trackingForEveryPageChange]
	);

	const trackSession = useCallback(async () => {
		if (isActiveTab) {
			const analyticsStore = AuthService.getAnalyticsSessionStore() as IAnalyticsStore;
			const response = await userSessionTrackingAnalytics();
			AuthService.setAnalyticsSessionStore({
				...analyticsStore,
				sessionStreamUserId: response.sessionStreamUserId,
				endTime: response.endTime
			});
		}
	}, [isActiveTab, userSessionTrackingAnalytics]);

	useEffect(() => {
		latestPathRef.current = pathname;
	}, [pathname]);

	useEffect(() => {
		if (!shouldTrack) return;

		const handleVisibilityChange = () => {
			const visibilityState = document.visibilityState;
			setIsActiveTab(visibilityState === 'visible');
			if (visibilityState === 'visible') {
				const analyticsStore = AuthService.getAnalyticsSessionStore() as IAnalyticsStore;
				const currentTime = new Date().setMilliseconds(0);
				const endTime = getValidDate(analyticsStore.endTime).setMilliseconds(0);

				if (currentTime > endTime) {
					AuthService.setAnalyticsSessionStore({
						...analyticsStore,
						sessionStreamUserId: '',
						startTime: new Date().toISOString(),
						endTime: new Date().toISOString()
					});
					if (intervalRef.current) {
						clearInterval(intervalRef.current);
						intervalRef.current = null;
					}
				}
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, [getValidDate, shouldTrack]);

	const startInterval = useCallback(() => {
		if (!intervalRef.current) {
			intervalRef.current = setInterval(() => {
				trackSession();
			}, intervalDuration);
		}
	}, [trackSession]);

	useEffect(() => {
		if (!shouldTrack) return;

		const analytics = AuthService.getAnalyticsSessionStore() as IAnalyticsStore;
		const currentTime = new Date().setMilliseconds(0);
		const endTime = getValidDate(analytics.endTime).setMilliseconds(0);
		const startTime = getValidDate(analytics.startTime).setMilliseconds(0);

		const isRefreshed = currentTime < endTime && startTime !== currentTime;

		const elapsedTime = currentTime - startTime;
		const timeUntilNextCall = intervalDuration - (elapsedTime % intervalDuration);

		if (analytics.sessionStreamUserId && isRefreshed) {
			if (!timeOutRef.current) {
				timeOutRef.current = setTimeout(() => {
					trackSession();
					startInterval();
				}, timeUntilNextCall);
			}
		} else if (!analytics.sessionStreamUserId && analytics.pageVisitsStreamId) {
			trackSession();
			startInterval();
		}

		return () => {
			clearInterval(intervalRef.current);
			clearTimeout(timeOutRef.current);
			clearTimeout(pageVisitTimeoutRef.current);
			intervalRef.current = null;
			timeOutRef.current = null;
			pageVisitTimeoutRef.current = null;
		};
	}, [getValidDate, shouldTrack, startInterval, trackSession]);

	useEffect(() => {
		if (!shouldTrack) return;

		const analyticsStore = AuthService.getAnalyticsSessionStore() as IAnalyticsStore;
		const initializeAnalytics = async () => {
			try {
				const sessionStreamData = await userSessionTrackingAnalytics();
				AuthService.setAnalyticsSessionStore({
					...analyticsStore,
					endTime: sessionStreamData.endTime,
					startTime: sessionStreamData.startTime,
					sessionStreamUserId: sessionStreamData.sessionStreamUserId
				});
				schedulePageVisit(latestPathRef.current, Number(process.env.REACT_APP_ANALYTICS_INITIAL_PAGE_VISIT_DELAY_MS || 9000));
			} catch (error) {
				console.error(error);
			}
		};
		if (isActiveTab && !analyticsStore.pageVisitsStreamId && !analyticsStore.sessionStreamUserId) {
			scheduleAnalytics(() => {
				if (!isActiveTab || !shouldTrack) return;
				initializeAnalytics();
			});
		}
	}, [isActiveTab, scheduleAnalytics, schedulePageVisit, shouldTrack, userSessionTrackingAnalytics]);

	useEffect(() => {
		if (!shouldTrack) return;

		schedulePageVisit(pathname);
	}, [pathname, schedulePageVisit, shouldTrack]);
};

const intervalDuration = 600000; // 10 minutes
